/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * Form Field Service
 * Single responsibility: Handle form field enhancement for any step
 */

import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useLazyFetchApiDataQuery } from '../../data-store/copilotApi';
import { updateStepFormData } from '../../data-store/copilotSlice';
import type { ParsedField } from '../../utils/templateParsers';

const isToolTipRequired = ['securitysystems.systemname'];

interface UseFormFieldLogicProps {
  stepName: string;
  stepFormData: Record<string, unknown>;
}

export const useFormFieldLogic = ({
  stepName,
  stepFormData,
}: UseFormFieldLogicProps) => {
  const dispatch = useDispatch();

  // Owner selection state (1 = User, 2 = User Groups) - for toggle group fields
  const [ownerSelection, setOwnerSelection] = useState<number>(() => {
    const ownerType = stepFormData['endpoints.ownerType'] as string;
    const toggleState = stepFormData.toggleState as string;

    // Use toggleState if available, otherwise fall back to ownerType
    const stateToCheck = toggleState || ownerType;

    return stateToCheck === 'User Group' || stateToCheck === 'User Groups'
      ? 2
      : 1;
  });

  // Use ref to track ownerSelection to avoid recreating functions
  const ownerSelectionRef = useRef(ownerSelection);

  ownerSelectionRef.current = ownerSelection;

  // API query for dropdown data
  const [fetchApiData] = useLazyFetchApiDataQuery();

  // Handle owner selection changes (for toggle group fields)
  const handleOwnerSelectionChange = useCallback(
    (value: number) => {
      setOwnerSelection(value);

      const ownerType = value === 1 ? 'User' : 'User Group';

      // Update form data with new owner type and clear dropdown value
      const updatedFormData = {
        ...stepFormData,
        'endpoints.ownerType': ownerType,
      };

      dispatch(
        updateStepFormData({
          stepName,
          formData: updatedFormData,
        })
      );
    },
    [dispatch, stepName, stepFormData]
  );

  // Create dropdown data fetcher
  const createDropdownDataFetcher = useCallback(
    (apiURL: string) =>
      async (params: { offset: number; max: number; findBy: string }) => {
        try {
          // Handle multiple endpoints separated by comma
          const endpoints = apiURL.split(',');
          let selectedEndpoint = endpoints[0]; // Default to first endpoint

          // Select endpoint based on current owner selection
          if (endpoints.length > 1) {
            // ownerSelectionRef.current: 1 = User, 2 = User Groups
            selectedEndpoint =
              ownerSelectionRef.current === 1 ? endpoints[0] : endpoints[1];
          }

          // Add /0 suffix if not present (based on the working curl)
          if (!selectedEndpoint.endsWith('/0')) {
            selectedEndpoint = `${selectedEndpoint.replace(/\/$/, '')}/0`;
          }

          const response = (await fetchApiData({
            apiKey: '/ECMv6/api',
            endpoint: selectedEndpoint,
            offset: params.offset,
            max: params.max,
            findBy: params.findBy,
          }).unwrap()) as
            | { id: string; value: string }[]
            | { data?: { id: string; value: string }[] };

          // Handle both direct array response and wrapped response
          let dataArray: { id: string; value: string }[] = [];

          if (Array.isArray(response)) {
            dataArray = response;
          } else if (response.data && Array.isArray(response.data)) {
            dataArray = response.data;
          }

          return dataArray.map((item) => ({
            label: item.value,
            value: item.id,
          }));
        } catch {
          return [];
        }
      },
    [fetchApiData]
  );

  // Enhance fields with General step specific logic
  const enhanceField = useCallback(
    (field: ParsedField, baseField: ParsedField) => {
      // Add tooltip logic for specific fields
      let enhancedField = { ...baseField };

      // Check if tooltip is required for this field
      if (
        field.attributeName !== undefined &&
        isToolTipRequired.includes(field.attributeName)
      ) {
        enhancedField = {
          ...enhancedField,
          isTooltip: true,
          tooltipText: 'INTEGRATION_COPILOT_TOOLTIP_TEXT',
          tooltipIconColor: '--color-foreground-info-subtle',
          placeholder: 'INTEGRATION_APPLICATION_NAME_PLACEHOLDER',
        };
      }

      if (field.attributeName === 'endpoints.description') {
        enhancedField = {
          ...enhancedField,
          placeholder: 'INTEGRATION_APPLICATION_DESCRIPTION_PLACEHOLDER',
        };
      }

      // Handle toggle group fields
      if (field.type === 'ToggleGroup') {
        return {
          ...enhancedField,
          // Use baseField.selection if it's already set, otherwise use ownerSelection
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          selection: (enhancedField as any).selection || ownerSelection,
          setSelection: handleOwnerSelectionChange,
        };
      }

      // Handle dropdown fields
      if (field.type === 'Dropdown' && field.apiURL) {
        // Handle object or string value
        const currentValue = enhancedField.defaultValue;
        let selectedValue: string | { id: string; value: string } = '';

        if (typeof currentValue === 'object' && currentValue) {
          selectedValue = currentValue;
        } else if (typeof currentValue === 'string' && currentValue) {
          selectedValue = currentValue;
        }

        return {
          ...enhancedField,
          key: `dropdown-${ownerSelection}`, // Force remount when owner selection changes
          fetchData: createDropdownDataFetcher(field.apiURL),
          config: { pageSize: 25 },
          isLoading: false,
          errorMessage: null,
          fieldDisabled: false,
          value: selectedValue,
        };
      }

      // Return enhanced field for other types
      return enhancedField;
    },
    [ownerSelection, handleOwnerSelectionChange, createDropdownDataFetcher]
  );

  return {
    enhanceField,
    ownerSelection,
    ownerSelectionRef,
  };
};

export default useFormFieldLogic;
